/**
 * Vuex module for managing membership related functionality
 */

export const namespaced = true;

const initState = () => {
  return {
    isLoading: false,
    membershipBalance: {
      membership: {
        level: "unknown",
        expireAt: null,
        total: 0,
      },
      balance: {
        total: 0,
        available: 0,
        used: 0,
        reserved: 0,
        expired: 0,
      },
    },
  };
};

export const state = initState();

export const mutations = {
  SET_LOADING(state, status) {
    state.isLoading = status;
  },
  SET_MEMBERSHIP_BALANCE(state, balance) {
    state.membershipBalance.balance = balance;
  },
};

export const getters = {
  isLoading: (state) => state.isLoading,
  membershipBalance: (state) => state.membershipBalance.balance,
};

export const actions = {
  /**
   * Fetches credit balance for an organization
   * @param {Object} context - Vuex action context
   * @param {Function} context.commit - Commit mutation
   * @param {Object} options - Options object
   * @param {string} options.organizationId - Organization ID
   * @param {Object} options.restClient - REST client instance
   * @param {boolean} options.withSubAccounts - Whether to include sub-accounts
   * @returns {Promise<Object>} - Credit balance information
   */
  async fetchCreditBalance(
    { commit },
    { organizationId, restClient, withSubAccounts = true }
  ) {
    try {
      commit("SET_LOADING", true);
      const response = await restClient.get(
        `/api/v1/organizations/account-balances?organizationId=${organizationId}&withSubAccounts=${withSubAccounts}`
      );

      const balances = response.data[0].balances;
      commit("SET_MEMBERSHIP_BALANCE", balances);

      return balances;
    } catch (error) {
      console.error("Error fetching credit balance:", error);
      // Keep the store in a safe zero state, but let callers know the
      // fetch failed so they can warn the user instead of treating the
      // zero balance as real data.
      commit("SET_MEMBERSHIP_BALANCE", {
        total: 0,
        available: 0,
        reserved: 0,
        used: 0,
        expired: 0,
      });
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  /**
   * Checks if a trip exists for a user and program
   * @param {Object} context - Vuex action context
   * @param {Function} context.commit - Commit mutation
   * @param {Object} options - Options object
   * @param {string} options.userId - User ID to check
   * @param {string} options.programId - Program ID to check
   * @param {Object} options.restClient - REST client instance
   * @param {Object} options.apolloClient - Apollo client for fallback (optional)
   * @returns {Promise<{exists: boolean, tripId: string|null}>} - Object with trip existence status and ID if found
   */
  async checkTripExists(
    { commit },
    { userId, programId, restClient, apolloClient }
  ) {
    try {
      commit("SET_LOADING", true);

      // Build search parameters
      const tripSearchParams = new URLSearchParams({
        userId,
        programId,
      }).toString();

      // Try REST API first
      const tripResponse = await restClient.get(`/api/v1/trips?${tripSearchParams}`);

      if (tripResponse && tripResponse.data && tripResponse.data.length > 0) {
        return {
          exists: true,
          tripId: tripResponse.data[0]._id,
        };
      }

      return {
        exists: false,
        tripId: null,
      };
    } catch (error) {
      console.error("Error checking trip existence via REST:", error);

      // Only try GraphQL fallback if apolloClient is provided
      if (apolloClient) {
        try {
          const { data: tripsData } = await apolloClient.query({
            query: require("../../graphql/query/GetTrips.gql"),
            variables: {
              filter: {
                userId,
                programId,
              },
            },
            fetchPolicy: "network-only",
          });

          const existingTrips = tripsData?.Trips?.trips || [];
          if (existingTrips.length > 0) {
            return {
              exists: true,
              tripId: existingTrips[0]._id,
            };
          }

          return {
            exists: false,
            tripId: null,
          };
        } catch (graphqlError) {
          console.error("GraphQL fallback also failed:", graphqlError);
          throw graphqlError;
        }
      }

      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  /**
   * Registers trips for an organization and assigns credits
   * @param {Object} context - Vuex action context
   * @param {Function} context.commit - Commit mutation
   * @param {Object} options - Options object
   * @param {string} options.organizationId - Organization ID
   * @param {Array<string>} options.tripIds - Array of trip IDs to register
   * @param {string} options.programName - Name of the program
   * @param {number} options.creditsPerTrip - Number of credits per trip
   * @param {Object} options.restClient - REST client instance
   * @returns {Promise<Object>} - Response with transaction details
   */
  async registerTrips(
    { commit },
    { organizationId, tripIds, programId, programName, creditsPerTrip, restClient }
  ) {
    try {
      commit("SET_LOADING", true);

      const response = await restClient.post("/api/v1/organizations/add-trips", {
        organizationId,
        tripIds,
        programId,
        programName,
        creditsPerTrip,
      });

      return response.data;
    } catch (error) {
      console.error("Error registering trips for organization:", error);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },
};
